import React, { useState } from "react";
import { Logo } from "../common/Logo.js";
import { AdminDashboard } from "./AdminDashboard.js";
import { AdminPages } from "./AdminPages.js";
import { AdminPosts } from "./AdminPosts.js";
import { AdminCurriculum } from "./AdminCurriculum.js";
import { AdminSubmissions } from "./AdminSubmissions.js";
import { AdminUsers } from "./AdminUsers.js";
import { AdminDatabase } from "./AdminDatabase.js";
import {
  LayoutDashboard,
  FileText,
  BookOpen,
  Inbox,
  Users,
  Database,
  LogOut,
  ExternalLink,
  Menu,
  X
} from "lucide-react";
export const AdminLayout = ({
  currentUser,
  onLogout,
  onBackToSite,
  submissions,
  posts,
  pages,
  curriculum,
  settings,
  users,
  onRefreshData,
  onStatusChange
}) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const newSubmissionsCount = submissions.filter((s) => s.status === "new").length;
  const navItems = [
    { id: "dashboard", label: "Dashboard Overview", icon: LayoutDashboard },
    { id: "pages", label: "Site Pages Manager", icon: FileText },
    { id: "posts", label: "Blog & Articles", icon: FileText },
    { id: "curriculum", label: "Curriculum & Classes", icon: BookOpen },
    {
      id: "submissions",
      label: "Inquiries & Submissions",
      icon: Inbox,
      badge: newSubmissionsCount > 0 ? newSubmissionsCount : void 0
    },
    { id: "users", label: "User Management (RBAC)", icon: Users, roleRestricted: "super_admin" },
    { id: "database", label: "MySQL Relational Studio", icon: Database }
  ];
  const visibleNavItems = navItems.filter(
    (item) => !item.roleRestricted || currentUser.role === item.roleRestricted
  );
  return <div className="min-h-screen bg-stone-100 flex flex-col md:flex-row font-sans">
      {
    /* Mobile Top Header */
  }
      <div className="md:hidden bg-emerald-950 text-white p-4 flex items-center justify-between border-b border-emerald-900 sticky top-0 z-40">
        <Logo size="sm" theme="dark" logoUrl={settings.logo_url} siteName={settings.site_name} />
        <div className="flex items-center gap-2">
          <button
    onClick={onBackToSite}
    className="p-1.5 rounded-lg text-emerald-200 hover:text-white"
    title="Public Site"
  >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
    onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
    className="p-1.5 rounded-lg text-emerald-200 hover:text-white"
  >
            {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {
    /* Sidebar Navigation */
  }
      <aside
    className={`${mobileSidebarOpen ? "block" : "hidden"} md:flex flex-col w-full md:w-64 bg-emerald-950 text-emerald-100 shrink-0 border-r border-emerald-900/60 min-h-screen sticky top-0 h-screen z-30`}
  >
        {
    /* Sidebar Brand Lockup */
  }
        <div className="p-5 border-b border-emerald-900/60 hidden md:block">
          <Logo size="md" theme="dark" logoUrl={settings.logo_url} siteName={settings.site_name} />
          <div className="mt-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-[11px] font-mono text-emerald-300 uppercase tracking-wider">
              {currentUser?.role ? currentUser.role.replace("_", " ") : "STAFF"}
            </span>
          </div>
        </div>

        {
    /* Navigation Items */
  }
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {visibleNavItems.map((item) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    return <button
      key={item.id}
      onClick={() => {
        setActiveTab(item.id);
        setMobileSidebarOpen(false);
      }}
      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${isActive ? "bg-amber-400 text-emerald-950 font-bold shadow-xs" : "text-emerald-200/90 hover:bg-emerald-900/40 hover:text-white"}`}
    >
                <div className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </div>

                {item.badge !== void 0 && <span
      className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${isActive ? "bg-emerald-950 text-amber-400" : "bg-amber-400 text-emerald-950"}`}
    >
                    {item.badge}
                  </span>}
              </button>;
  })}
        </nav>

        {
    /* Sidebar Footer User Info & Exit */
  }
        <div className="p-4 border-t border-emerald-900/60 space-y-2 bg-emerald-950">
          <div className="px-2 py-1.5 text-xs">
            <div className="font-semibold text-white truncate">{currentUser.name}</div>
            <div className="text-[11px] text-emerald-300/80 truncate font-mono">{currentUser.email}</div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
    onClick={onBackToSite}
    className="flex-1 py-1.5 px-2 bg-emerald-900/60 hover:bg-emerald-900 rounded-lg text-emerald-200 hover:text-white text-xs font-medium flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
  >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Public Site</span>
            </button>

            <button
    onClick={onLogout}
    className="py-1.5 px-3 bg-red-950/40 hover:bg-red-900/60 text-red-200 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors cursor-pointer"
    title="Sign Out"
  >
              <LogOut className="w-3.5 h-3.5" />
              <span>Exit</span>
            </button>
          </div>
        </div>
      </aside>

      {
    /* Main Admin Workspace */
  }
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {activeTab === "dashboard" && <AdminDashboard
    currentUser={currentUser}
    submissions={submissions}
    posts={posts}
    pages={pages}
    curriculum={curriculum}
    settings={settings}
    onNavigateTab={(tab) => setActiveTab(tab)}
    onStatusChange={onStatusChange}
  />}

          {activeTab === "pages" && <AdminPages
    pages={pages}
    settings={settings}
    currentUser={currentUser}
    onPageUpdated={onRefreshData}
  />}

          {activeTab === "posts" && <AdminPosts
    posts={posts}
    currentUser={currentUser}
    onPostUpdated={onRefreshData}
  />}

          {activeTab === "curriculum" && <AdminCurriculum
    curriculum={curriculum}
    currentUser={currentUser}
    onCurriculumUpdated={onRefreshData}
  />}

          {activeTab === "submissions" && <AdminSubmissions
    submissions={submissions}
    currentUser={currentUser}
    onSubmissionsUpdated={onRefreshData}
  />}

          {activeTab === "users" && <AdminUsers
    users={users}
    currentUser={currentUser}
    onUsersUpdated={onRefreshData}
  />}

          {activeTab === "database" && <AdminDatabase />}
        </div>
      </main>
    </div>;
};
